import { useEffect } from 'react';
import { Heart, Target, Award } from 'lucide-react';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="relative min-h-[60vh] flex items-center justify-center overflow-hidden" 
        data-testid="about-hero-section"
        style={{
          background: 'linear-gradient(135deg, #fff5e6 0%, #ffe4d6 100%)'
        }}
      >
        <div className="hero-content fade-in-up relative z-10">
          <h1 className="hero-title text-[#8b4513] drop-shadow-lg" data-testid="about-title">About JFRAI Family</h1>
          <div className="decorative-line bg-[#8b4513]"></div>
          <p className="hero-subtitle text-[#6b4423] drop-shadow-md" data-testid="about-subtitle">
            A Family Legacy of Culinary Excellence and Hospitality
          </p>
        </div>
      </section>

      {/* Story Section with Images */}
      <section className="section" style={{ background: 'white' }} data-testid="about-story-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="section-title">Our Story</h2>
          <div className="decorative-line"></div>
          
          {/* Story Grid with Images */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8 items-center">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1641529148223-093a513ed44e?w=800&q=80" 
                alt="Traditional Caribbean Cooking Heritage"
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div className="space-y-6 text-lg text-[#6b4423] leading-relaxed">
            <p>
              JFRAI Family Catering and Event Planning is proudly a <strong className="text-[#8b4513]">family-owned and 
              family-run business</strong> born from a deep passion for bringing people together through exceptional 
              food and memorable experiences. Our journey began with a simple belief: that every event, no matter 
              the size, deserves to be special.
            </p>
            <p>
              What makes us truly unique is our authentic heritage. One part of the JFRAI family brings 
              <strong className="text-[#8b4513]"> Caribbean and Southern roots passed down from their ancestors</strong>, 
              giving us an invaluable connection to the traditional recipes, cooking techniques, and flavors that 
              define these beloved cuisines. This heritage isn't just learned—it's lived, breathed, and lovingly 
              shared through every dish we create. The authentic spices, cooking methods, and family recipes handed 
              down through generations make it easy for us to craft meals that taste like home.
            </p>
            <p>
              Specializing in American, Southern, and Caribbean cuisine, we blend these time-honored traditional 
              recipes with modern culinary techniques to create dishes that delight the senses and warm the heart. 
              Each dish we serve carries the authentic flavors, cultural richness, and love that have been passed 
              down through our family for generations.
            </p>
            <p>
              From intimate family gatherings to grand corporate events, we approach every occasion with
              the same dedication to quality, service, and creating unforgettable moments. As a family business, 
              we treat every event like we're cooking for our own family—with care, authenticity, and a commitment 
              to excellence. Our team is committed to making your vision come to life, handling every detail so 
              you can enjoy your special day.
            </p>
          </div>
          </div>
          
          {/* Team Quote - Painting Style */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12 items-center">
            <div className="space-y-4 lg:order-2">
              <h3 className="text-2xl font-bold text-[#8b4513]">Our Team</h3>
              <p className="text-lg text-[#6b4423] leading-relaxed">
                Behind every delicious meal and memorable event is our dedicated team of culinary professionals 
                and event specialists. We work together as a family to ensure every detail is perfect, from the 
                first consultation to the final bite.
              </p>
            </div>
            <div 
              className="relative overflow-hidden rounded-2xl shadow-2xl lg:order-1"
              style={{
                background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 50%, #ff9a9e 100%)',
                padding: '60px 40px',
                minHeight: '400px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                position: 'relative',
                border: '8px solid #8b4513',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3), inset 0 0 30px rgba(255,255,255,0.3)'
              }}
            >
              {/* Painting texture overlay */}
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg width="200" height="200" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noiseFilter"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" stitchTiles="stitch"/%3E%3C/filter%3E%3Crect width="100%25" height="100%25" filter="url(%23noiseFilter)" opacity="0.15"/%3E%3C/svg%3E")',
                  opacity: 0.3,
                  mixBlendMode: 'overlay',
                  pointerEvents: 'none'
                }}
              />
              
              {/* Quote marks decoration */}
              <div 
                style={{
                  fontSize: '80px',
                  color: 'rgba(139, 69, 19, 0.2)',
                  lineHeight: '0.5',
                  marginBottom: '20px',
                  fontFamily: 'Georgia, serif',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                "
              </div>
              
              {/* Main Quote */}
              <blockquote 
                style={{
                  fontSize: '28px',
                  fontWeight: 'bold',
                  color: '#8b4513',
                  fontFamily: 'Playfair Display, Georgia, serif',
                  lineHeight: '1.6',
                  textShadow: '2px 2px 4px rgba(255,255,255,0.8)',
                  position: 'relative',
                  zIndex: 1,
                  marginBottom: '20px'
                }}
              >
                Family is not just an important thing, it's everything. Together we create, together we serve, together we shine.
              </blockquote>
              
              {/* Attribution */}
              <div 
                style={{
                  fontSize: '18px',
                  color: '#6b4423',
                  fontStyle: 'italic',
                  fontFamily: 'Georgia, serif',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                — The JFRAI Family
              </div>
              
              {/* Closing quote marks */}
              <div 
                style={{
                  fontSize: '80px',
                  color: 'rgba(139, 69, 19, 0.2)',
                  lineHeight: '0.5',
                  marginTop: '20px',
                  fontFamily: 'Georgia, serif',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                "
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section" style={{ background: '#fff5e6' }} data-testid="about-values-section">
        <h2 className="section-title">Our Values</h2>
        <div className="decorative-line"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
          <div className="card text-center" data-testid="about-value-quality">
            <div className="flex justify-center mb-4 text-[#d2691e]">
              <Award size={48} />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-[#8b4513]">Quality</h3>
            <p className="text-[#6b4423]">
              We never compromise on the quality of our ingredients or service. Every dish is prepared
              with care and attention to detail.
            </p>
          </div>
          <div className="card text-center" data-testid="about-value-passion">
            <div className="flex justify-center mb-4 text-[#d2691e]">
              <Heart size={48} />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-[#8b4513]">Passion</h3>
            <p className="text-[#6b4423]">
              Our love for food and hospitality drives everything we do. We pour our hearts into every
              event we cater.
            </p>
          </div>
          <div className="card text-center" data-testid="about-value-excellence">
            <div className="flex justify-center mb-4 text-[#d2691e]">
              <Target size={48} />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-[#8b4513]">Excellence</h3>
            <p className="text-[#6b4423]">
              We strive for excellence in every aspect of our service, from planning to execution and
              beyond.
            </p>
          </div>
        </div>
      </section>

      {/* Specialties Section */}
      <section className="section" style={{ background: 'white' }} data-testid="about-specialties-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="section-title">What We Specialize In</h2>
          <div className="decorative-line"></div>
          
          {/* Image and Specialties Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8 items-start">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src="https://images.pexels.com/photos/7648311/pexels-photo-7648311.jpeg?w=800&q=80" 
                alt="Professional Catering Service"
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div>
              <div className="grid grid-cols-1 gap-6">
            <div className="menu-item" data-testid="about-specialty-catering">
              <h3 className="text-xl font-bold mb-2 text-[#8b4513]">Full-Service Catering</h3>
              <p className="text-[#6b4423]">
                From appetizers to desserts, we provide complete catering solutions for any event.
              </p>
            </div>
            <div className="menu-item" data-testid="about-specialty-event-planning">
              <h3 className="text-xl font-bold mb-2 text-[#8b4513]">Event Planning</h3>
              <p className="text-[#6b4423]">
                Our experienced team handles all aspects of event coordination and management.
              </p>
            </div>
            <div className="menu-item" data-testid="about-specialty-custom-menus">
              <h3 className="text-xl font-bold mb-2 text-[#8b4513]">Custom Menus</h3>
              <p className="text-[#6b4423]">
                We create personalized menus tailored to your preferences and dietary requirements.
              </p>
            </div>
            <div className="menu-item" data-testid="about-specialty-delivery">
              <h3 className="text-xl font-bold mb-2 text-[#8b4513]">Delivery & Setup</h3>
              <p className="text-[#6b4423]">
                Professional delivery, setup, and service staff for a seamless experience.
              </p>
            </div>
            <div className="menu-item" data-testid="about-specialty-farm-to-table">
              <h3 className="text-xl font-bold mb-2 text-[#8b4513]">🌾 Farm to Table</h3>
              <p className="text-[#6b4423]">
                Fresh, locally-sourced ingredients from trusted farms for superior quality and flavor.
              </p>
            </div>
            <div className="menu-item" data-testid="about-specialty-dietary">
              <h3 className="text-xl font-bold mb-2 text-[#8b4513]">🌾🚫 Dietary Accommodations</h3>
              <p className="text-[#6b4423]">
                Gluten-free, vegan, vegetarian, and allergen-friendly options available for all events.
              </p>
            </div>
          </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;